import React from 'react';
import { useDispatch } from 'react-redux';
import { motion } from 'framer-motion';
import * as Lucide from 'lucide-react';
import { useMagicLinkAuth, signalCloseOriginalTab } from '@mental-mint/shared-utils';
import { BasePageProps } from '../../Page/PageTypes';
import { PageAuthError, AuthErrorType } from '../PageAuthError/PageAuthError';
import './PageMagicLinkCallback.css';

// Delay before navigating to game after successful login (allows user to see success message)
const SUCCESS_NAVIGATION_DELAY_MS = 1500;

export interface PageMagicLinkCallbackProps extends BasePageProps {
  /** Callback to navigate back to the main game after successful login */
  onLoginSuccess?: () => void;
  /** Callback to navigate to a specific page */
  navigateToPage?: (pageId: string, props?: any) => void;
}

/**
 * PageMagicLinkCallback - Handles magic link verification from email
 * 
 * This page is shown when a user clicks a magic link in their email.
 * It verifies the token and shows a success/error message.
 * 
 * Works on both web and mobile:
 * - Web: Shows success, signals other tabs to close login modal, then navigates back
 * - Mobile: Shows success then automatically returns to game
 */
export const PageMagicLinkCallback: React.FC<PageMagicLinkCallbackProps> = ({ 
  onLoginSuccess,
  onRequestClose,
  navigateToPage
}) => {
  const dispatch = useDispatch();
  
  // Use the hook with navigation callback for mobile compatibility
  const { isVerifying, userEmail, error, errorType } = useMagicLinkAuth({ 
    dispatch,
    onSuccessNavigate: onLoginSuccess
  });

  // Signal original tab to close and navigate when auth succeeds
  // Must be called unconditionally (Rules of Hooks)
  React.useEffect(() => {
    if (userEmail) {
      // Signal other tabs to close (web-only, harmless on mobile)
      signalCloseOriginalTab();
      
      // Navigate to game after short delay to show success message
      const timer = setTimeout(() => {
        if (onLoginSuccess) {
          onLoginSuccess();
        }
      }, SUCCESS_NAVIGATION_DELAY_MS);
      
      return () => clearTimeout(timer);
    }
  }, [userEmail, onLoginSuccess]);

  // Show loading state while verifying
  if (isVerifying) {
    return (
      <div className="magic-link-callback-container">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="magic-link-callback-card"
        >
          <div className="magic-link-callback-spinner" />
          <h2 className="magic-link-callback-title">Verifying your login...</h2>
          <p className="magic-link-callback-text">Please wait a moment</p>
        </motion.div>
      </div>
    );
  }

  // Show error state FIRST - reuse PageAuthError component
  // Check error before success to handle race conditions in StrictMode
  if (error && errorType) {
    return (
      <div className="magic-link-callback-container">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="magic-link-callback-card"
        >
          <PageAuthError
            errorType={errorType === 'expired' ? AuthErrorType.Expired : AuthErrorType.Invalid}
            onRequestNewLink={() => {
              // Close the error modal first
              if (onRequestClose) {
                onRequestClose();
              }
              // Then navigate to login page
              if (navigateToPage) {
                navigateToPage('login', {
                  title: "Request New Login Link",
                  subtitle: "Enter your email to receive a fresh login link."
                });
              }
            }}
          />
        </motion.div>
      </div>
    );
  }

  // Show success state
  if (userEmail) {
    return (
      <div className="magic-link-callback-container">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          className="magic-link-callback-card magic-link-callback-success"
        >
          <Lucide.CheckCircle size={64} className="magic-link-callback-success-icon" />
          <h2 className="magic-link-callback-title">Successfully Logged In!</h2>
          <p className="magic-link-callback-text">
            Welcome back, <strong>{userEmail}</strong>
          </p>
          <p className="magic-link-callback-subtext">
            Starting game...
          </p>
        </motion.div>
      </div>
    );
  }

  // Fallback (shouldn't normally reach this)
  return null;
};
